import { z } from 'zod';
import { assetsAssetIdSchema, assetsCommandIdSchema } from './assets-asset.contract.js';
export const assetsFleetAssignmentsCapability = 'assets.fleet-assignments.v1';
export const assetsFleetOdometerCapability = 'assets.fleet-odometer.v1';
export const assetsFleetAssignmentSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    assetId: assetsAssetIdSchema,
    membershipId: z.string().uuid(),
    assignedAt: z.string().datetime({ offset: true }),
    assignedByMembershipId: z.string().uuid(),
    returnedAt: z.string().datetime({ offset: true }).nullable(),
    returnedByMembershipId: z.string().uuid().nullable(),
    assignmentReason: z.string().trim().min(10).max(500),
    returnReason: z.string().trim().min(10).max(500).nullable(),
});
export const assetsAssignFleetCommandSchema = z.strictObject({
    kind: z.literal('assign'),
    commandId: assetsCommandIdSchema,
    assetId: assetsAssetIdSchema,
    membershipId: z.string().uuid(),
    assignedAt: z.string().datetime({ offset: true }),
    reason: z.string().trim().min(10).max(500),
});
export const assetsReturnFleetCommandSchema = z.strictObject({
    kind: z.literal('return'),
    commandId: assetsCommandIdSchema,
    assetId: assetsAssetIdSchema,
    assignmentId: z.string().uuid(),
    returnedAt: z.string().datetime({ offset: true }),
    reason: z.string().trim().min(10).max(500),
});
export const assetsFleetAssignmentCommandSchema = z.discriminatedUnion('kind', [
    assetsAssignFleetCommandSchema,
    assetsReturnFleetCommandSchema,
]);
export const assetsFleetAssignmentResponseSchema = z.strictObject({
    data: assetsFleetAssignmentSchema,
    command: z.strictObject({ commandId: assetsCommandIdSchema, idempotent: z.boolean() }),
});
export const assetsFleetAssignmentsResponseSchema = z.strictObject({
    data: z.array(assetsFleetAssignmentSchema),
});
export const assetsOdometerSourceSchema = z.enum([
    'manual', 'end-of-day', 'correction', 'oil-change',
]);
export const assetsOdometerFlagSchema = z.enum([
    'backwards', 'daily-delta', 'jump', 'chronology',
]);
export const assetsFleetPolicySchema = z.strictObject({
    maximumDailyMiles: z.number().int().min(1).max(5_000),
    maximumJumpMiles: z.number().int().min(1).max(100_000),
    oilChangeIntervalMiles: z.number().int().min(500).max(50_000),
    oilChangeWarningMiles: z.number().int().min(0).max(10_000),
    oilChangeIntervalMonths: z.number().int().min(1).max(60),
    oilChangeDateWarningDays: z.number().int().min(0).max(90),
    expiryWarningDays: z.number().int().min(0).max(180),
});
export const assetsRecordOdometerCommandSchema = z.strictObject({
    commandId: assetsCommandIdSchema,
    assetId: assetsAssetIdSchema,
    reading: z.number().int().nonnegative().max(9_999_999),
    recordedAt: z.string().datetime({ offset: true }),
    source: assetsOdometerSourceSchema,
    sourceReference: z.string().trim().min(1).max(160).nullable(),
});
export const assetsFleetOdometerReadingSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    assetId: assetsAssetIdSchema,
    reading: z.number().int().nonnegative(),
    previousMileage: z.number().int().nonnegative().nullable(),
    applied: z.boolean(),
    flag: assetsOdometerFlagSchema.nullable(),
    source: assetsOdometerSourceSchema,
    sourceReference: z.string().nullable(),
    maximumDailyMilesSnapshot: z.number().int().positive(),
    maximumJumpMilesSnapshot: z.number().int().positive(),
    recordedByMembershipId: z.string().uuid(),
    recordedAt: z.string().datetime({ offset: true }),
    createdAt: z.string().datetime({ offset: true }),
});
export const assetsFleetOdometerResponseSchema = z.strictObject({
    data: assetsFleetOdometerReadingSchema,
    command: z.strictObject({ commandId: assetsCommandIdSchema, idempotent: z.boolean() }),
});
export const assetsFleetOdometerHistoryResponseSchema = z.strictObject({
    data: z.array(assetsFleetOdometerReadingSchema),
});
const maintenanceDimensionSchema = z.strictObject({
    status: z.enum(['ok', 'due-soon', 'overdue', 'unknown']),
    dueAtMileage: z.number().int().nonnegative().nullable(),
    dueAtDate: z.string().nullable(),
    remainingMiles: z.number().int().nullable(),
    remainingDays: z.number().int().nullable(),
});
export const assetsFleetMaintenanceStatusSchema = z.strictObject({
    oil: maintenanceDimensionSchema,
    registration: maintenanceDimensionSchema,
    insurance: maintenanceDimensionSchema,
    overall: z.enum(['ok', 'due-soon', 'overdue', 'unknown']),
});
const millisecondsPerDay = 86_400_000;
function utcDate(value) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(Date.UTC(year ?? 0, (month ?? 0) - 1, day ?? 0));
}
function isoDate(value) {
    return value.toISOString().slice(0, 10);
}
function addCalendarMonths(value, months) {
    const source = utcDate(value);
    const targetYear = source.getUTCFullYear();
    const targetMonth = source.getUTCMonth() + months;
    const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
    return isoDate(new Date(Date.UTC(targetYear, targetMonth, Math.min(source.getUTCDate(), lastDay))));
}
function remainingDays(dueAtDate, asOfDate) {
    if (dueAtDate === null)
        return null;
    return Math.ceil((utcDate(dueAtDate).getTime() - utcDate(asOfDate).getTime()) /
        millisecondsPerDay);
}
function maintenanceStatus(remainingMiles, remainingDateDays, mileageWarning, dateWarning) {
    if ((remainingMiles !== null && remainingMiles <= 0) ||
        (remainingDateDays !== null && remainingDateDays <= 0))
        return 'overdue';
    if ((remainingMiles !== null && remainingMiles <= mileageWarning) ||
        (remainingDateDays !== null && remainingDateDays <= dateWarning))
        return 'due-soon';
    if (remainingMiles === null && remainingDateDays === null)
        return 'unknown';
    return 'ok';
}
export function calculateAssetsFleetMaintenanceStatus(input) {
    const oilDueAtMileage = input.lastOilChangeMileage === null
        ? null
        : input.lastOilChangeMileage + input.policy.oilChangeIntervalMiles;
    const oilRemainingMiles = oilDueAtMileage === null || input.currentMileage === null
        ? null
        : oilDueAtMileage - input.currentMileage;
    const oilDueAtDate = input.lastOilChangeDate === null
        ? null
        : addCalendarMonths(input.lastOilChangeDate, input.policy.oilChangeIntervalMonths);
    const oilRemainingDays = remainingDays(oilDueAtDate, input.asOfDate);
    const registrationRemainingDays = remainingDays(input.registrationExpiry, input.asOfDate);
    const insuranceRemainingDays = remainingDays(input.insuranceExpiry, input.asOfDate);
    const oil = {
        status: maintenanceStatus(oilRemainingMiles, oilRemainingDays, input.policy.oilChangeWarningMiles, input.policy.oilChangeDateWarningDays),
        dueAtMileage: oilDueAtMileage,
        dueAtDate: oilDueAtDate,
        remainingMiles: oilRemainingMiles,
        remainingDays: oilRemainingDays,
    };
    const registration = {
        status: maintenanceStatus(null, registrationRemainingDays, 0, input.policy.expiryWarningDays),
        dueAtMileage: null,
        dueAtDate: input.registrationExpiry,
        remainingMiles: null,
        remainingDays: registrationRemainingDays,
    };
    const insurance = {
        status: maintenanceStatus(null, insuranceRemainingDays, 0, input.policy.expiryWarningDays),
        dueAtMileage: null,
        dueAtDate: input.insuranceExpiry,
        remainingMiles: null,
        remainingDays: insuranceRemainingDays,
    };
    const rank = { ok: 0, unknown: 1, 'due-soon': 2, overdue: 3 };
    const overall = [oil.status, registration.status, insurance.status].reduce((current, candidate) => rank[candidate] > rank[current] ? candidate : current, 'ok');
    return assetsFleetMaintenanceStatusSchema.parse({ oil, registration, insurance, overall });
}
export function evaluateAssetsOdometerReading(input) {
    if (input.currentMileage === null)
        return { applied: true, flag: null };
    if (input.reading < input.currentMileage)
        return { applied: false, flag: 'backwards' };
    const delta = input.reading - input.currentMileage;
    if (delta > input.policy.maximumJumpMiles)
        return { applied: false, flag: 'jump' };
    if (input.mileageUpdatedAt !== null) {
        const previous = Date.parse(input.mileageUpdatedAt);
        const next = Date.parse(input.recordedAt);
        if (!Number.isFinite(previous) || !Number.isFinite(next) || next < previous) {
            return { applied: false, flag: 'chronology' };
        }
        const elapsedDays = Math.max(1, Math.round((next - previous) / 86_400_000));
        if (delta > input.policy.maximumDailyMiles * elapsedDays) {
            return { applied: false, flag: 'daily-delta' };
        }
    }
    return { applied: true, flag: null };
}
//# sourceMappingURL=assets-fleet.contract.js.map